<template>
  <div class="requests-control">
    <button class="req-btn" @click="open" :title="$i18n.t('requestControls')">
      <i class="material-icons">tune</i>
    </button>

    <info-modal :visible.sync="show">
      <template #header>
        <div class="d-flex align-items-center w-100">
          <i class="material-icons mr-2">tune</i>
          <h4 class="m-0">{{ $i18n.t('requestControls') }}</h4>
        </div>
      </template>

      <template #body>
        <div>
          <!-- Default user request (cannot remove) -->
          <div class="req-item mb-3 p-2 border rounded" :class="{ 'is-lag': isLag(config.requests[0]) }">
            <div class="d-flex align-items-center justify-content-between mb-1">
              <strong>{{ $i18n.t('defaultUserRequest') }}</strong>
              <span v-if="isLag(config.requests[0])" class="badge badge-danger">
                {{ $i18n.t('lag') || 'Lag' }}
              </span>
            </div>

            <div class="form-group mt-2">
              <label>{{ $i18n.t('intervalSeconds') }}</label>
              <input type="number" class="form-control" v-model.number="config.requests[0].intervalSec" min="1" />
            </div>

            <small class="text-muted d-block">
              <span v-if="statuses[reqKey(config.requests[0])]">
                {{ $i18n.t('last') || 'Last' }}:
                {{ ms(statuses[reqKey(config.requests[0])].lastDurationMs) }},
                {{ $i18n.t('next') || 'Next' }}:
                {{ eta(statuses[reqKey(config.requests[0])].nextRunAt) }}
              </span>
            </small>

            <small class="text-muted">{{ $i18n.t('userRequestHint') }}</small>
          </div>


          <hr />

          <!-- Dynamic requests (INLINE) -->
          <div class="req-list">
            <div v-for="(req, idx) in config.requests.slice(1)" :key="idx + 1" class="req-item mb-3 p-2 border rounded"
              :class="{ 'is-lag': isLag(req) }" title="Lagging request runs slower than its interval">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <strong class="text-capitalize">{{ req.type || $i18n.t('type') }}</strong>
                <span v-if="isLag(req)" class="badge badge-danger">{{ $i18n.t('lag') || 'Lag' }}</span>
              </div>

              <div class="form-group">
                <label>{{ $i18n.t('type') }}</label>
                <select v-model="req.type" class="form-control">
                  <option disabled value="">{{ $i18n.t('selectType') }}</option>
                  <option value="dashboard">{{ $i18n.t('dashboard') }}</option>
                  <option value="variable">{{ $i18n.t('variable') }}</option>
                  <option value="device">{{ $i18n.t('device') }}</option>
                </select>
              </div>

              <div class="form-group" v-if="req.type">
                <label>{{ $i18n.t('selectItem') }}</label>
                <select v-model="req.id" class="form-control">
                  <option v-for="item in getList(req.type)" :key="item.id" :value="item.id">
                    {{ item.name || ('#' + item.id) }}
                  </option>
                </select>
              </div>

              <div class="form-group">
                <label>{{ $i18n.t('intervalSeconds') }}</label>
                <input type="number" class="form-control" v-model.number="req.intervalSec" min="1" />
              </div>

              <!-- tiny status line -->
              <small class="text-muted d-block">
                <span v-if="statuses[reqKey(req)]">
                  {{ $i18n.t('last') || 'Last' }}: {{ ms(statuses[reqKey(req)].lastDurationMs) }},
                  {{ $i18n.t('next') || 'Next' }}: {{ eta(statuses[reqKey(req)].nextRunAt) }}
                </span>
              </small>

              <button class="btn btn-danger btn-sm mt-2" @click="removeRequest(idx + 1)">
                {{ $i18n.t('remove') }}
              </button>
            </div>
          </div>

          <button class="btn btn-success btn-sm" @click="addRequest" style="color: white !important;">
            {{ $i18n.t('addRequest') }}
          </button>
        </div>
      </template>

      <template #footer>
        <div class="w-100 d-flex justify-content-end">
          <button class="btn btn-secondary mr-2" @click="resetConfig">{{ $i18n.t('reset') }}</button>
          <button class="btn btn-primary" @click="applyConfig">{{ $i18n.t('apply') }}</button>
        </div>
      </template>
    </info-modal>
  </div>
</template>

<script>
import { requestManager } from "@/domain/GlobalFunctions/requestManager";

export default {
  name: "RequestsControl",
  data() {
    return {
      show: false,
      config: { requests: [] },
      dashboards: [],
      variables: [],
      devices: [],
      statuses: {},       // key => status from requestManager
      statusPoller: null, // interval id
    };
  },
  methods: {
    open() {
      this.show = true;
      if (!this.dashboards.length || !this.devices.length || !this.variables.length) {
        this.loadLists();
      }
    },

    stopAll() {
      requestManager.stopAll();
    },

    async loadLists() {
      try {
        const dashSvc = new this.$dashboardService();
        const devSvc = new this.$deviceService();
        const varSvc = new this.$variableService();
        const [dashes, devs, vars] = await Promise.all([
          dashSvc.listByPagination(0, 1000000),
          devSvc.listByPagination(0, 1000000),
          varSvc.listByPagination(0, 1000000),
        ]);
        this.dashboards = dashes || [];
        this.devices = devs || [];
        this.variables = vars || [];
      } catch (e) {
        console.log(e)
      }
    },
    getList(type) {
      if (type === "dashboard") return this.dashboards;
      if (type === "variable") return this.variables;
      if (type === "device") return this.devices;
      return [];
    },
    ensureDefaultUserReq() {
      if (!this.config.requests?.length || this.config.requests[0].type !== "user") {
        this.config.requests = [{ type: "user", intervalSec: 1 }, ...(this.config.requests || [])];
      }
    },
    addRequest() {
      this.config.requests.push({ type: "", id: null, intervalSec: 10 });
      this.saveConfig();
    },
    removeRequest(index) {
      this.config.requests.splice(index, 1);
      this.saveConfig();
    },
    resetConfig() {
      this.config.requests = [{ type: "user", intervalSec: 1 }];
      this.saveConfig();
      requestManager.stopAll();
      this.pullStatuses(); // refresh immediately
    },
    applyConfig() {
      this.saveConfig();
      this.startRequests();
      this.show = false;
    },
    saveConfig() {
      localStorage.setItem("requestsConfig", JSON.stringify(this.config));
    },
    loadConfig() {
      const saved = localStorage.getItem("requestsConfig");
      if (saved) this.config = JSON.parse(saved);
      this.ensureDefaultUserReq();
    },
    startRequests() {
      requestManager.startRequests(this.config.requests, this);
      this.pullStatuses(); // refresh immediately
    },
    // --- status helpers ---
    reqKey(req) {
      return `${req.type}:${req.id || "self"}`;
    },
    pullStatuses() {
      this.statuses = requestManager.getStatuses() || {};
    },
    isLag(req) {
      const s = this.statuses[this.reqKey(req)];
      return !!(s && s.lag);
    },
    ms(v) {
      if (v == null) return '—';
      return `${v}ms`;
    },
    eta(ts) {
      if (!ts) return '—';
      const inMs = ts - Date.now();
      if (inMs <= 0) return this.$i18n.t('etaNow');
      const s = Math.round(inMs / 1000);
      return this.$i18n.t('etaInSeconds', { s });
    }
  },
  created() {
    this.loadConfig();
    this.startRequests();
    // poll statuses every 1s (lightweight map)
    this.statusPoller = setInterval(this.pullStatuses, 1000);
  },
  beforeDestroy() {
    if (this.statusPoller) clearInterval(this.statusPoller);
  }
};
</script>

<style scoped lang="scss">
@import url("https://fonts.googleapis.com/icon?family=Material+Icons");

.req-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 .75rem;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, .08);
  color: #fff;
  cursor: pointer;
}

.req-btn:hover {
  background: rgba(255, 255, 255, .14);
}

.req-btn .material-icons {
  font-size: 20px;
}

.req-item {
  background: rgba(255, 255, 255, .05);
  min-width: 260px;
  max-width: 340px;
  display: inline-flex;
  flex-direction: column;
}

/* inline layout for the dynamic requests */
.req-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: stretch;
}

/* lag styling */
.is-lag {
  background: rgba(220, 53, 69, 0.12);
  /* red-ish */
  border-color: rgba(220, 53, 69, 0.6) !important;
}

.badge-danger {
  background: #dc3545;
  color: #fff;
}
</style>
